import Bluebird from "bluebird";
import * as request from "superagent";

import {
  addTokenToStorageAction,
  restartNetActivityAction,
  startNetActivityAction,
  stopNetActivityAction,
  stopNetActivityWithErrorAction,
} from "../actions/netActivity";
import { acquireTokenSilent, isInteractionRequiredError, pca } from "../authProvider";
import { ConcurrencyTarget } from "../models/concurrencyTargetEnum";
import { store } from "../reduxStore";
import { trackDependency, trackException } from "../services/applicationInsights";

import { getVersionLabel } from "./version";

import type { ServerErrorType } from "../constants/errors";
import type { ExtraOptions } from "../services/tee";

interface RequestError {
  message: string;
  status?: number;
  response?: {
    text?: string;
    body?: {
      type?: string;
    };
  };
}

interface ExtendedExtraOptions extends ExtraOptions {
  responseType?: string;
}

const RETRY_COUNT = 3;
const singletonRequests: Record<string, Bluebird<request.Response>> = {};

Bluebird.config({
  cancellation: true,
});

export enum HttpMethod {
  Get = "GET",
  Post = "POST",
  Head = "HEAD",
  Put = "PUT",
  Delete = "DELETE",
  Connect = "CONNECT",
  Options = "OPTIONS",
  Trace = "TRACE",
  Patch = "PATCH",
}

function withoutBodyMethod(url: string, extraOptions?: ExtraOptions, method = HttpMethod.Get) {
  const { dispatch } = store;
  const requestId = startNetActivityAction(dispatch, url, extraOptions?.singletonKey);

  if (window.useAzureADAuth) {
    return resolveToken(dispatch, requestId).then((token) =>
      sendWithoutBody(url, method, extraOptions, "Bearer", token.accessToken, dispatch, requestId),
    );
  }

  return sendWithoutBody(url, method, extraOptions, "", "", dispatch, requestId);
}

export function get(url: string, extraOptions?: ExtraOptions) {
  return withoutBodyMethod(url, extraOptions, HttpMethod.Get);
}

export function deleteEndpoint(url: string, extraOptions?: ExtraOptions) {
  return withoutBodyMethod(url, extraOptions, HttpMethod.Delete);
}

export function withBodyMethod(
  url: string,
  body: object,
  extraOptions?: ExtraOptions,
  method = HttpMethod.Post,
) {
  const { dispatch } = store;
  const requestId = startNetActivityAction(dispatch, url);

  if (window.useAzureADAuth) {
    return resolveToken().then((token) =>
      sendWithBody(
        url,
        body,
        method,
        extraOptions,
        "Bearer",
        token.accessToken,
        dispatch,
        requestId,
      ),
    );
  }

  return sendWithBody(url, body, method, extraOptions, "", "", dispatch, requestId);
}

export function post(url: string, body: object, extraOptions?: ExtraOptions) {
  return withBodyMethod(url, body, extraOptions, HttpMethod.Post);
}

export function patch(url: string, body: object, extraOptions?: ExtraOptions) {
  return withBodyMethod(url, body, extraOptions, HttpMethod.Patch);
}

export function put(url: string, body: object, extraOptions?: ExtraOptions) {
  return withBodyMethod(url, body, extraOptions, HttpMethod.Put);
}

export function sendWithoutBody(
  _url: string,
  method: HttpMethod | string,
  extraOptions?: ExtraOptions,
  accessTokenType?: string,
  accessToken?: string,
  dispatch?: Function,
  requestId?: string,
) {
  const url = getUrl(_url, extraOptions);
  const headers = getHeaders(extraOptions, accessTokenType, accessToken);
  // let r: request.SuperAgentRequest;
  // Creating a promise wrapper for our superagent request allows us to avoid
  // the premature error throw behavior that is exhibited by the first failure
  // before the .retry() routine starts
  if (extraOptions?.singletonKey) {
    const existingRequest = singletonRequests[extraOptions.singletonKey];
    if (existingRequest) {
      restartNetActivityAction(dispatch, requestId, extraOptions.singletonKey);
      existingRequest.cancel();
    }
    singletonRequests[extraOptions.singletonKey] = createCancellableRequest(
      url,
      headers,
      method,
      extraOptions,
      dispatch,
      requestId,
    );
    return singletonRequests[extraOptions.singletonKey];
  }
  const promise = createCancellableRequest(url, headers, method, extraOptions, dispatch, requestId);
  return promise;
}

export function sendWithBody(
  _url: string,
  body: object,
  method: HttpMethod | string,
  extraOptions?: ExtraOptions,
  accessTokenType?: string,
  accessToken?: string,
  dispatch?: Function,
  requestId?: string,
) {
  let count = 1;
  const url = getUrl(_url, extraOptions);
  const headers = getHeaders(extraOptions, accessTokenType, accessToken);
  const startTime = Date.now();

  const promise = new Bluebird<request.Response>((ok, rej) => {
    const retry = function (err: RequestError, res: request.Response) {
      // GUI-2813, do not retry 5xx
      const is5xx = res && res?.status >= 500 && res?.status !== 501;
      if (is5xx || (count === RETRY_COUNT && err)) {
        stopNetActivityWithErrorAction(
          dispatch,
          requestId,
          is5xx ? "Server error" : "Too many retries",
          "",
          res?.body?.type,
        );
        rej(err);
        return false;
      }
      count++;
      // returning undefined will let the logic continue with superagent
    };

    request
      .default(method, url)
      .set(headers)
      .send(body)
      .retry(RETRY_COUNT, retry)
      .timeout({
        deadline: 60000, // allow 1 minute to finish loading
        response: 60000, //  wait 60 seconds for the server to start sending
      })
      .then((res: request.Response) => checkHeaders(res, extraOptions))
      .then((res: request.Response) => {
        const duration = Date.now() - startTime;

        // Track successful dependency call
        trackDependency("HTTP", `${method} ${url}`, url, duration, true, res.status);

        stopNetActivityAction(dispatch, requestId, res.status);
        ok(res);
        return res;
      })
      .catch((err: RequestError) => {
        const duration = Date.now() - startTime;

        // Track failed dependency call
        trackDependency("HTTP", `${method} ${url}`, url, duration, false, err.status);

        // Track HTTP errors as exceptions for critical failures
        if (err.status && err.status >= 500) {
          trackException(new Error(`HTTP ${err.status}: ${err.message}`), {
            url,
            method,
            status: err.status,
            requestId,
            duration,
            errorType: "HTTPError",
          });
        }

        stopNetActivityWithErrorAction(
          dispatch,
          requestId,
          err.message,
          err.status?.toString(),
          err.response?.body?.type as ServerErrorType,
        );

        if (err.status === 401) {
          pca.logoutRedirect();
        }

        // checking if a more detailed error message is returned from the server
        try {
          if (err.response?.text) {
            const errorDetails = JSON.parse(err.response.text);
            if (errorDetails) {
              const errCopy = { ...err };
              if (errorDetails.title) {
                errCopy.message = errorDetails.title;
              }
              rej(errCopy);
            }
          }
        } catch {
          // swallow the error
        }

        rej(err);
      });
  });
  return promise;
}

function resolveToken(dispatch?: Function, requestId?: string) {
  return acquireTokenSilent()
    .then((tokenResponse) => {
      // update token in Tee
      const { TC } = window;
      if (TC) {
        const previousToken = TC.settings && TC.settings.authorizationKey;
        if (previousToken !== tokenResponse.accessToken) {
          TC.settings.authorizationKey = tokenResponse.accessToken;
        }
      }

      addTokenToStorageAction(dispatch, tokenResponse);

      return tokenResponse;
    })
    .catch((error) => {
      if (error.message === "Unauthorized" || isInteractionRequiredError(error)) {
        console.log("Need authorization");
      }

      stopNetActivityWithErrorAction(dispatch, requestId, error.message, "");

      addTokenToStorageAction(dispatch, { error });

      throw error;
    });
}

function getUrl(_url: string, extraOptions: ExtraOptions | undefined) {
  const url = extraOptions?.useExactPath ? _url : `${getPrefix()}/${_url}`;
  return url;
}

function checkHeaders(result: request.Response, extraOptions: ExtraOptions | undefined) {
  if (extraOptions) {
    if (extraOptions.concurrencyTarget) {
      return checkConcurrencyTarget(result, extraOptions.concurrencyTarget);
    }
  }
  return result;
}

function checkConcurrencyTarget(_result: request.Response, concurrencyTarget: ConcurrencyTarget) {
  const result = _result;
  const concurrency = result.header.etag || result.header["x-tc-concurrencyversion"];
  if (concurrency) {
    switch (concurrencyTarget) {
      case ConcurrencyTarget.Encounter:
        if (result.body) {
          result.body.ConcurrencyVersion = concurrency;
        } else {
          result.body = {
            ConcurrencyVersion: concurrency,
          };
        }
        break;
      case ConcurrencyTarget.Changeset:
        result.body = {
          changes: result.body,
          concurrencyVersion: concurrency,
        };
        break;
      default:
        console.error(`concurrency wasn't handled! ${concurrency}`);
        break;
    }
  }
  return result;
}

function getHeaders(
  extraOptions: ExtraOptions | undefined,
  accessTokenType?: string,
  accessToken?: string,
) {
  const mandatoryHeaders = {
    From: `apollo.${process.env.REACT_APP_GIT_COMMIT_HASH ?? "local"}@trucode.com`,
    "Client-Version": getVersionLabel(process.env.REACT_APP_BRANCH_NAME ?? "local"),
  };

  const authorizationTokenType = accessTokenType || window.TC.settings.authorizationTokenType;
  const authorizationKey = accessToken || window.TC.settings.authorizationKey;

  const defaultHeaders: Record<string, string> = {
    Authorization: `${authorizationTokenType} ${authorizationKey}`,
    Accept: "application/json",
  };

  if (!extraOptions?.skipContentTypeHeader) {
    defaultHeaders["Content-Type"] = "application/json";
  }

  let headers = {};

  if (extraOptions) {
    headers =
      extraOptions.useExactHeaders && extraOptions.headers ?
        { ...extraOptions.headers, ...mandatoryHeaders }
      : { ...defaultHeaders, ...extraOptions.headers, ...mandatoryHeaders };
  }

  return headers;
}

function getPrefix() {
  const webServiceBaseUrl: Record<string, string> = {
    beta: "https://cloud.trucode.com/",
    dev: "https://wsdev.trucode.com/",
    dev_ci: "https://wsdev.trucode.com/",
    local: "http://wslocal.trucode.com/",
    prod: "https://ws.trucode.com/",
    qa: "",
    stage: "https://wsstage.trucode.com/",
  };
  const webServiceBaseApiPrefix = "v3";
  if (
    window.TC.settings.useCustomEnv &&
    window.TC.settings.customEnvUri &&
    window.TC.settings.customEnvUri.length
  ) {
    return window.TC.settings.customEnvUri;
  }
  return webServiceBaseUrl[window.TC.env] + webServiceBaseApiPrefix;
}

function createCancellableRequest(
  url: string,
  headers: Record<string, string>,
  method: HttpMethod | string,
  extraOptions: ExtraOptions | undefined,
  dispatch?: Function,
  requestId?: string,
) {
  let count = 1;

  return new Bluebird<request.Response>((ok, rej) => {
    const retry = function (err: RequestError, res: request.Response) {
      // GUI-2813, do not retry 5xx
      const is5xx = res && res?.status >= 500 && res?.status !== 501;
      if (is5xx || (count === RETRY_COUNT && err)) {
        stopNetActivityWithErrorAction(
          dispatch,
          requestId,
          is5xx ? "Server error" : "Too many retries",
          "",
          res?.body?.type,
        );
        rej(err);
        return false;
      }
      count++;
      // returning undefined will let the logic continue with superagent
    };

    // r = request(method, url)
    // try once and then 3 retries, 4 times total
    // if response times out that's response timeout x 4 before showing error (~40sec)
    const createRequest = request
      .default(method, url)
      .set(headers)
      .retry(RETRY_COUNT, retry)
      .timeout({
        deadline: 60000, // allow 1 minute to finish loading
        response: 60000, //  wait 60 seconds for the server to start sending
      });

    if ((extraOptions as ExtendedExtraOptions).responseType === "blob") {
      createRequest.responseType("blob");
    }

    createRequest
      .then((response: request.Response) => checkHeaders(response, extraOptions))
      .then((response: request.Response) => {
        stopNetActivityAction(dispatch, requestId, response.status);
        ok(response);
        return response;
      })
      .catch((error: RequestError) => {
        stopNetActivityWithErrorAction(
          dispatch,
          requestId,
          error.message,
          error.status?.toString(),
          error.response?.body?.type as ServerErrorType,
        );

        if (error.status === 401) {
          pca.logoutRedirect();
        }

        if (error.status && error.status >= 500) {
          rej(error);
        }

        rej(error);
      });
  });
}
